import { createContext, useCallback, use, useEffect, useMemo, useRef, useState } from "react";
import type { FC, ReactNode } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigationBlock } from "../shared/useNavigationBlock";
import { UnsavedChangesDialog } from "./shared/UnsavedChangesDialog";
import type { Answer, AnswerRequest, OnSave, QuestionCard, SaveInput } from "./types";
import { isLabelAndValueArray } from "./types";
import type { FileOwner } from "../api/files";

export type UnansweredMandatorySection = {
  sectionName: string;
  unansweredMandatoryQuestions: { questionId: string; header: string }[];
};
/** This context is used to pass extra state to the DynamicForm components.
 * Information that is not a part of the react-hook-form */
export type FormState = {
  savedAnswers: Answer[];
  isReadOnly: boolean;
  isSaveError: boolean;
  isSaving: boolean;
  mandatoryQuestionErrorMessage?: string | undefined;
  saveInput: SaveInput;
  navigationBlockState: ReturnType<typeof useNavigationBlock>["state"];
  numberOfUnsavedChanges: number;
  disableQuestionsByTag: Record<NonNullable<QuestionCard["tag"]>, boolean>;
  fileOwner: FileOwner | undefined;
};
const DynamicFormStateContext = createContext<FormState | undefined>(undefined);
const UnsavedChangeReporterContext = createContext<(isDirty: boolean) => void>(() => {});

type PendingReset = { questionId: string; value: AnswerRequest["value"] };

type SaveProps =
  | { isReadOnly?: boolean; onSave: OnSave; isSaveError: boolean; isSaving: boolean }
  | { isReadOnly: true; onSave?: undefined; isSaveError?: undefined; isSaving?: undefined };

type DynamicFormProviderProps = {
  children: ReactNode | ((props: { formState: FormState }) => ReactNode);
  answers?: Answer[];
  mandatoryQuestionErrorMessage?: string | undefined;
  disableQuestionsByTag?: FormState["disableQuestionsByTag"];
  fileOwner?: FileOwner;
} & SaveProps;

export const DynamicFormProvider: FC<DynamicFormProviderProps> = ({
  children,
  answers = [],
  onSave,
  isReadOnly = false,
  isSaveError = false,
  isSaving = false,
  mandatoryQuestionErrorMessage,
  disableQuestionsByTag,
  fileOwner,
}) => {
  const pendingResetsRef = useRef<Array<PendingReset>>([]);

  /** useMemo: We only want a new reference to values when answers changed and not when parent re-renders for other reasons,
   *  since that would reset the values of the form to initial answers.  */

  type SavedValues = Record<QuestionCard["id"], AnswerRequest["value"] | undefined>;
  const savedValues: SavedValues = useMemo(
    () =>
      Object.fromEntries(
        answers.map(({ questionId, value }) => [
          questionId,
          isLabelAndValueArray(value) ? value.map(({ value }) => value) : value,
        ])
      ),
    [answers]
  );

  const methods = useForm({ defaultValues: savedValues });

  useEffect(() => {
    if (isReadOnly) methods.reset();
  }, [isReadOnly, methods]);

  useEffect(() => {
    /** Do not update form values if a new saving has started.
     * This is to avoid jumping back to prev value while saving if toggle same input field quickly. */
    if (!isSaving) {
      pendingResetsRef.current.forEach(({ questionId, value }) =>
        methods.resetField(questionId, { defaultValue: value })
      );
      pendingResetsRef.current = [];
      /**
       * We reset the entire form since values other than the ones the user has interacted can have changed:
       * For example, if the user removed a value triggering a follow up, the follow up question answer will be removed by the backend.
       * We also keep the dirty values, so that we don't reset text input fields to their initial default value in case they are in edit mode and an other question is answered.
       */

      /** Set fields that are NOT in savedValues(aka answers) to null. This ensures all fields are reset, not just those in savedValues. */
      const formKeys = Object.keys(methods.getValues());
      const resetMap = Object.fromEntries(formKeys.map(questionId => [questionId, null]));
      methods.reset({ ...resetMap, ...savedValues }, { keepDirtyValues: true });
    }
  }, [isSaving, methods, pendingResetsRef, savedValues]);

  const [hasUnsavedDraft, setHasUnsavedDraft] = useState(false);

  /** We do not want to count the field that is being saved as an unsaved change */
  const numberOfUnsavedChanges =
    Math.max(0, Object.entries(methods.formState.dirtyFields).length - (isSaving ? 1 : 0)) + (hasUnsavedDraft ? 1 : 0);
  const navigationBlock = useNavigationBlock(numberOfUnsavedChanges > 0);

  const saveInput: SaveInput = useCallback(
    async (answer, { rollbackOnError = true } = {}) => {
      if (isReadOnly) {
        throw new Error(
          `Should not be able to save in readonly mode. Make sure input with "${answer.type}" is disabled`
        );
      }

      if (!onSave) {
        throw new Error("If you want to save, you must provide an onSave function. Otherwise make the form read only.");
      }

      return await onSave(answer)
        .then(() => {
          pendingResetsRef.current = pendingResetsRef.current.filter(reset => reset.questionId !== answer.questionId);
          pendingResetsRef.current.push({ questionId: answer.questionId, value: answer.value });
        })
        .catch(error => {
          if (rollbackOnError) methods.resetField(answer.questionId);
          throw error;
        });
    },
    [isReadOnly, onSave, methods]
  );

  const formState: FormState = useMemo(
    () => ({
      savedAnswers: answers,
      isReadOnly,
      isSaveError,
      isSaving,
      saveInput,
      mandatoryQuestionErrorMessage,
      disableQuestionsByTag: disableQuestionsByTag ?? {},
      fileOwner,
      navigationBlockState: navigationBlock.state,
      numberOfUnsavedChanges,
    }),
    [
      answers,
      isReadOnly,
      isSaveError,
      isSaving,
      saveInput,
      mandatoryQuestionErrorMessage,
      disableQuestionsByTag,
      fileOwner,
      navigationBlock.state,
      numberOfUnsavedChanges,
    ]
  );

  return (
    <DynamicFormStateContext value={formState}>
      {navigationBlock.state === "blocked" && (
        <UnsavedChangesDialog
          onCancel={() => navigationBlock.cancel()}
          onProceed={() => {
            methods.reset();
            navigationBlock.proceed();
          }}
        />
      )}
      <UnsavedChangeReporterContext value={setHasUnsavedDraft}>
        <FormProvider {...methods}>{typeof children === "function" ? children({ formState }) : children}</FormProvider>
      </UnsavedChangeReporterContext>
    </DynamicFormStateContext>
  );
};

export const useDynamicFormState = () => {
  const context = use(DynamicFormStateContext);
  if (!context) throw new Error('"useDynamicFormState" can only be used within a "DynamicFormProvider');
  return context;
};

export const useReportUnsavedChange = (isDirty: boolean) => {
  const setHasUnsavedDraft = use(UnsavedChangeReporterContext);
  useEffect(() => {
    setHasUnsavedDraft(isDirty);
    return () => setHasUnsavedDraft(false);
  }, [setHasUnsavedDraft, isDirty]);
};
